import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import type { CategoryStat } from '../../types';
import { getCategoryMeta } from '../../hooks/useAnalytics';

interface CategoryBreakdownProps {
  data: CategoryStat[];
  totalSpent?: number;
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: import('../../types').CategoryStat }> }) {
  if (!active || !payload?.length) return null;
  const { category, total, percentage } = payload[0].payload;
  const meta = getCategoryMeta(category || "");
  return (
    <div className="card px-3 py-2 shadow-xl text-sm">
      <p className="text-slate-300">
        {meta.icon} {meta.label}
      </p>
      <p className="text-white font-semibold">€{total.toFixed(2)}</p>
      <p className="text-slate-400">{percentage.toFixed(1)}%</p>
    </div>
  );
}

export default function CategoryBreakdown({ data }: CategoryBreakdownProps) {
  if (!data.length) return null;

  const top5 = data.slice(0, 5);

  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-slate-300 mb-5">Category Breakdown</h3>
      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* Donut */}
        <div className="w-40 h-40 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={top5}
                cx="50%"
                cy="50%"
                innerRadius={46}
                outerRadius={68}
                paddingAngle={3}
                dataKey="total"
              >
                {top5.map(entry => (
                  <Cell
                    key={entry.category}
                    fill={getCategoryMeta(entry.category || "").color}
                    stroke="transparent"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-3 w-full">
          {top5.map(stat => {
            const meta = getCategoryMeta(stat.category || "");
            return (
              <div key={stat.category} className="flex items-center gap-3">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: meta.color }}
                />
                <span className="text-xs text-slate-400 flex-1 truncate">
                  {meta.icon} {meta.label}
                </span>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-medium text-slate-200">€{stat.total.toFixed(2)}</p>
                  <p className="text-[10px] text-slate-500">{stat.percentage.toFixed(1)}%</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
